import { Add } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { Tag } from '../../../types/Tag';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AddTagModal from './AddTagModal';
import AccordionSection from '../../AccordionSection';

interface ITagChipProps {
  tag: Tag;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
}

function TagChip({ tag, onEdit, onDelete }: ITagChipProps) {
  const { disabled } = useContext(DisableContext);
  const label = _.join(_.filter(_.map([tag.preference_value, tag.text], _.trim)), ': ');
  const chipProps = disabled ? { disabled: true } : { onDelete: () => onDelete(tag.uuid), onClick: () => onEdit(tag.uuid) };

  return <Chip clickable label={label} {...chipProps} />;
}

interface ITagsSectionProps {
  collapseId: UserSettingsCollapseKeys;
  tags: Tag[];
  setTags: (tags: Tag[]) => void;
}

export default function TagsSection({ collapseId, tags, setTags }: ITagsSectionProps) {
  const { disabled } = useContext(DisableContext);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const handleAddOrUpdateTag = (tag: Tag) => {
    const updatedModels = _.includes(_.map(tags, 'uuid'), tag.uuid) ? _.map(tags, (model) => (model.uuid === tag.uuid ? tag : model)) : [...tags, tag];

    setTags(updatedModels);
    setModalOpen(false);
    setSelectedTag(null);
  };

  const onEdit = (uuid: string) => {
    setSelectedTag(_.find(tags, { uuid }) || null);
    setModalOpen(true);
  };

  const onDelete = (uuid: string) => {
    setTags(_.reject(tags, { uuid }));
  };

  return (
    <>
      <AccordionSection
        title="Tags"
        collapseId={collapseId}
        hasData={tags.length > 0}
        headerElements={
          disabled ? null : (
            <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)}>
              Add
            </ResponsiveButton>
          )
        }
        id="tags"
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {_.map(tags, (tag) => (
            <TagChip key={tag.uuid} tag={tag} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </Box>
      </AccordionSection>
      <AddTagModal isOpen={isModalOpen} onCancel={() => setModalOpen(false)} onAdd={handleAddOrUpdateTag} tag={selectedTag} />
    </>
  );
}
